import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, GripVertical, Upload, Image as ImageIcon, Camera, PlayCircle, Youtube, Video } from "lucide-react";
import api, { formatApiError, resolveMedia } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
}

export default function AdminBauchroniken() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState({ header_image_url: "", header_video_url: "", header_video_thumbnail_url: "", intro: "" });
  const [pageSaving, setPageSaving] = useState(false);
  const [pageUploading, setPageUploading] = useState(false);
  const [dragId, setDragId] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [l, p] = await Promise.all([api.get("/bauchroniken/all"), api.get("/bauchroniken/page")]);
      setItems(l.data || []);
      setPage(p.data || { header_image_url: "", header_video_url: "", header_video_thumbnail_url: "", intro: "" });
    } catch (e) {
      toast.error("Konnte Bauchroniken nicht laden.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const uploadHeader = async (file) => {
    if (!file) return;
    setPageUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPage((p) => ({ ...p, header_image_url: data.url }));
      toast.success("Header-Bild hochgeladen — bitte speichern.");
    } catch (e) {
      toast.error("Upload fehlgeschlagen: " + (formatApiError(e.response?.data?.detail) || e.message));
    } finally {
      setPageUploading(false);
    }
  };

  const uploadVideoThumb = async (file) => {
    if (!file) return;
    setPageUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPage((p) => ({ ...p, header_video_thumbnail_url: data.url }));
      toast.success("Video-Vorschaubild hochgeladen — bitte speichern.");
    } catch (e) {
      toast.error("Upload fehlgeschlagen: " + (formatApiError(e.response?.data?.detail) || e.message));
    } finally {
      setPageUploading(false);
    }
  };

  const detectVideoProvider = (url) => {
    const u = (url || "").toLowerCase();
    if (u.includes("youtube.com") || u.includes("youtu.be")) return "YouTube";
    if (u.includes("vimeo.com")) return "Vimeo";
    return "";
  };

  const savePage = async () => {
    setPageSaving(true);
    try {
      await api.put("/bauchroniken/page/settings", page);
      toast.success("Übersichts-Einstellungen gespeichert.");
    } catch (e) {
      toast.error("Konnte nicht speichern: " + (formatApiError(e.response?.data?.detail) || e.message));
    } finally {
      setPageSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Eintrag „${item.title}" wirklich löschen?`)) return;
    try {
      await api.delete(`/bauchroniken/${item.id}`);
      toast.success("Eintrag gelöscht.");
      loadAll();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const persistOrder = async (list) => {
    setItems(list);
    try {
      await api.post("/bauchroniken/reorder", { ids: list.map((x) => x.id) });
    } catch { toast.error("Reihenfolge konnte nicht gespeichert werden."); }
  };

  const onDragStart = (id) => (e) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    const srcIdx = items.findIndex((x) => x.id === dragId);
    const tgtIdx = items.findIndex((x) => x.id === targetId);
    if (srcIdx < 0 || tgtIdx < 0) return;
    const next = [...items];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, moved);
    persistOrder(next);
    setDragId(null);
  };

  return (
    <div className="space-y-10" data-testid="admin-bauchroniken-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-2">Inhalte</p>
          <h1 className="text-3xl font-semibold text-neutral-900 mb-2">Bauchroniken</h1>
          <p className="text-sm text-neutral-500 max-w-2xl">Foto- &amp; Videodokumentation der Bauentwicklung. Reihenfolge per Drag-and-Drop, Status Entwurf/Veröffentlicht pro Eintrag.</p>
        </div>
        <button
          onClick={() => nav("/admin/bauchroniken/new")}
          data-testid="bauchroniken-new-btn"
          className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-strong))] text-white px-4 py-2.5 text-sm hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> Neuer Eintrag
        </button>
      </div>

      {/* Page settings (header + intro + hero video) */}
      <section className="border border-brand-line bg-white" data-testid="bauchroniken-page-settings">
        <div className="px-6 py-4 border-b border-brand-line">
          <h2 className="text-sm font-semibold text-neutral-900">Übersichtsseite</h2>
        </div>
        <div className="p-6 grid lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div>
              <label className="block text-xs text-neutral-500 mb-1.5">Header-Bild</label>
              <div className="aspect-[16/9] bg-neutral-100 border border-brand-line overflow-hidden">
                {page.header_image_url ? (
                  <img src={resolveMedia(page.header_image_url)} alt="Header" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs"><ImageIcon className="h-6 w-6" /></div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-3">
                <label className="inline-flex items-center gap-2 border border-brand-line px-3 py-2 text-sm cursor-pointer hover:bg-neutral-50">
                  <Upload className="h-4 w-4" /> {pageUploading ? "Lädt…" : "Bild hochladen"}
                  <input type="file" accept="image/*" className="hidden" disabled={pageUploading} onChange={(e) => uploadHeader(e.target.files?.[0])} data-testid="bauchroniken-header-upload" />
                </label>
                {page.header_image_url && (
                  <button onClick={() => setPage((p) => ({ ...p, header_image_url: "" }))} className="text-xs text-neutral-500 hover:text-red-600 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Entfernen</button>
                )}
              </div>
            </div>

            {/* NEW: Hero video (YouTube / Vimeo) */}
            <div className="border-t border-brand-line pt-6">
              <label className="block text-xs text-neutral-500 mb-1.5 inline-flex items-center gap-1.5">
                <Video className="h-3.5 w-3.5" /> Hero-Video (optional · YouTube oder Vimeo)
              </label>
              <input
                type="url"
                value={page.header_video_url || ""}
                onChange={(e) => setPage((p) => ({ ...p, header_video_url: e.target.value }))}
                placeholder="https://www.youtube.com/watch?v=… oder https://vimeo.com/…"
                data-testid="bauchroniken-header-video-url"
                className="w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))] focus:ring-1 focus:ring-[hsl(var(--brand-green-strong))]"
              />
              {page.header_video_url && (
                <p className={`text-[11px] mt-1.5 ${detectVideoProvider(page.header_video_url) ? "text-emerald-700" : "text-amber-700"}`}>
                  {detectVideoProvider(page.header_video_url)
                    ? <>Erkannt: <strong>{detectVideoProvider(page.header_video_url)}</strong>. Das Video ersetzt das Header-Bild auf der öffentlichen Übersicht.</>
                    : "URL nicht erkannt — nur YouTube- oder Vimeo-Links werden unterstützt."}
                </p>
              )}
              {/* Custom video poster / thumbnail */}
              <div className="mt-4">
                <label className="block text-[11px] text-neutral-500 mb-1.5">Vorschaubild (optional)</label>
                <div className="flex items-start gap-3">
                  <div className="w-32 aspect-[16/9] bg-neutral-100 border border-brand-line overflow-hidden shrink-0">
                    {page.header_video_thumbnail_url ? (
                      <img src={resolveMedia(page.header_video_thumbnail_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-300"><PlayCircle className="h-5 w-5" /></div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="inline-flex items-center gap-2 border border-brand-line px-3 py-1.5 text-xs cursor-pointer hover:bg-neutral-50">
                      <Upload className="h-3.5 w-3.5" /> {page.header_video_thumbnail_url ? "Austauschen" : "Hochladen"}
                      <input type="file" accept="image/*" className="hidden" disabled={pageUploading} onChange={(e) => uploadVideoThumb(e.target.files?.[0])} data-testid="bauchroniken-header-video-thumb" />
                    </label>
                    {page.header_video_thumbnail_url && (
                      <button onClick={() => setPage((p) => ({ ...p, header_video_thumbnail_url: "" }))} className="text-xs text-neutral-500 hover:text-red-600">Entfernen</button>
                    )}
                    <button onClick={() => setPage((p) => ({ ...p, header_video_url: "", header_video_thumbnail_url: "" }))} className="text-[11px] text-neutral-400 hover:text-red-600">Video komplett entfernen</button>
                  </div>
                </div>
                <p className="text-[11px] text-neutral-400 mt-1.5">Wird als Standbild angezeigt, bevor der Besucher auf Play klickt. Ohne eigenes Bild wird das YouTube/Vimeo-Standard-Thumbnail verwendet.</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col">
            <label className="block text-xs text-neutral-500 mb-1.5">Einführungstext</label>
            <RichTextEditor value={page.intro} onChange={(v) => setPage((p) => ({ ...p, intro: v }))} minHeight={180} testid="bauchroniken-intro-editor" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-brand-line flex justify-end">
          <button onClick={savePage} disabled={pageSaving} data-testid="bauchroniken-page-save" className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-strong))] text-white px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50">
            {pageSaving ? "Speichere…" : "Übersicht speichern"}
          </button>
        </div>
      </section>

      {/* Entries list */}
      <section>
        <h2 className="text-sm font-semibold text-neutral-900 mb-3">Einträge</h2>
        {loading ? (
          <div className="text-sm text-neutral-500">Lade…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-brand-line p-10 text-center text-sm text-neutral-500" data-testid="bauchroniken-empty">
            Noch keine Einträge. Klicken Sie oben rechts auf „Neuer Eintrag".
          </div>
        ) : (
          <div className="border border-brand-line bg-white divide-y divide-brand-line">
            {items.map((it) => {
              const imgs = (it.media || []).filter((m) => m.type === "image").length;
              const vids = (it.media || []).filter((m) => m.type === "video").length;
              const cover = it.header_image_url || (it.media || []).find((m) => m.type === "image")?.url;
              return (
                <div
                  key={it.id}
                  draggable
                  onDragStart={onDragStart(it.id)}
                  onDragOver={onDragOver}
                  onDrop={onDrop(it.id)}
                  data-testid={`bauchroniken-row-${it.slug}`}
                  className={`flex items-center gap-4 px-4 py-3 hover:bg-neutral-50 ${dragId === it.id ? "opacity-50" : ""}`}
                >
                  <GripVertical className="h-4 w-4 text-neutral-300 shrink-0 cursor-grab" />
                  <div className="w-16 h-12 bg-neutral-100 border border-brand-line overflow-hidden shrink-0">
                    {cover ? <img src={resolveMedia(cover)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-neutral-300"><ImageIcon className="h-4 w-4" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/admin/bauchroniken/${it.id}`} className="text-sm font-medium text-neutral-900 truncate hover:text-[hsl(var(--brand-green-strong))]">{it.title}</Link>
                      <span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border ${it.status === "published" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                        {it.status === "published" ? "Veröffentlicht" : "Entwurf"}
                      </span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5 inline-flex items-center gap-3">
                      {it.date && <span>{it.date}</span>}
                      {imgs > 0 && <span className="inline-flex items-center gap-1"><Camera className="h-3 w-3" /> {imgs}</span>}
                      {vids > 0 && <span className="inline-flex items-center gap-1"><PlayCircle className="h-3 w-3" /> {vids}</span>}
                      <span className="text-neutral-400">/{it.slug}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Link to={`/admin/bauchroniken/${it.id}`} className="p-1.5 text-neutral-500 hover:text-neutral-900" title="Bearbeiten" data-testid={`bauchroniken-edit-${it.slug}`}><Edit2 className="h-4 w-4" /></Link>
                    <button onClick={() => remove(it)} className="p-1.5 text-neutral-500 hover:text-red-600" title="Löschen" data-testid={`bauchroniken-delete-${it.slug}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
